import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { getSupportedMimeType } from "@/lib/mediaRecorder/utils";
import { LocalRecordingCapture } from "@/lib/offline-recording/capture";
import {
  OWNED_UPLOAD_MAX_BYTES,
  readStorageStatus,
  requestPersistentStorage,
  type StorageStatus,
  UPLOAD_LIMIT_WARNING_RATIO,
} from "@/lib/offline-recording/limits";
import {
  currentOfflineAccount,
  useOfflineRecordingStore,
} from "@/store/offlineRecording.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import type { OfflineRecordingNotices } from "./OfflineRecordingBanner";

const subscribeOnline = (listener: () => void): (() => void) => {
  window.addEventListener("online", listener);
  window.addEventListener("offline", listener);
  return () => {
    window.removeEventListener("online", listener);
    window.removeEventListener("offline", listener);
  };
};

/** `navigator.onLine`, updated on the browser's online/offline events. */
export const useOnlineStatus = (): boolean =>
  useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine !== false,
    () => true,
  );

const STORAGE_CHECK_MS = 5 * 60 * 1000;

export interface LocalCaptureControls {
  /** Starts the chunked copy on this stream (first call only). */
  ensure: (stream: MediaStream) => void;
  pause: () => void;
  resume: () => void;
  /** Stops and waits for the last chunk. */
  stop: () => Promise<void>;
  notices: OfflineRecordingNotices;
  online: boolean;
}

/**
 * Owns the {@link LocalRecordingCapture} of one recorder. The capture is
 * released when the recorder unmounts, which makes an unsubmitted recording
 * show up under Pending recordings right away.
 */
export const useLocalCapture = ({
  enabled,
  realtime,
  onCapture,
}: {
  enabled: boolean;
  realtime: boolean;
  onCapture?: (capture: LocalRecordingCapture) => void;
}): LocalCaptureControls => {
  const captureRef = useRef<LocalRecordingCapture | null>(null);
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  const [storageFailed, setStorageFailed] = useState(false);
  const [bytes, setBytes] = useState(0);
  const [storage, setStorage] = useState<StorageStatus | null>(null);
  const online = useOnlineStatus();

  const ensure = useCallback(
    (stream: MediaStream): void => {
      if (!enabled || captureRef.current) return;
      const scope = currentOfflineAccount();
      if (!scope) return;
      const settings = useRecordingSettingsStore.getState();
      let mimeType: string | undefined;
      try {
        mimeType = getSupportedMimeType();
      } catch {}
      const capture = new LocalRecordingCapture({
        uid: scope.uid,
        accountPublicId: scope.accountPublicId,
        mimeType,
        capture: {
          language: settings.inputLanguage,
          realtime,
          diarize: settings.diarize,
          providerId: settings.transcriptionNoRealtimeProvider,
        },
        onStorageError: () => setStorageFailed(true),
        onProgress: (progress) => setBytes(progress.bytes),
      });
      captureRef.current = capture;
      void requestPersistentStorage();
      void readStorageStatus().then(setStorage);
      void capture.start(stream).then(() => {
        void useOfflineRecordingStore.getState().refresh();
      });
      onCaptureRef.current?.(capture);
    },
    [enabled, realtime],
  );

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => {
      if (captureRef.current?.state === "recording")
        void readStorageStatus().then(setStorage);
    }, STORAGE_CHECK_MS);
    return () => clearInterval(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // A reload, a closed tab or a phone switching apps: save the audio
    // recorded since the last slice before the page may be frozen or gone.
    const saveNow = (): void => captureRef.current?.saveNow();
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") saveNow();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", saveNow);
    window.addEventListener("beforeunload", saveNow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", saveNow);
      window.removeEventListener("beforeunload", saveNow);
    };
  }, [enabled]);

  useEffect(
    () => () => {
      const capture = captureRef.current;
      captureRef.current = null;
      if (capture)
        void capture
          .release()
          .then(() => useOfflineRecordingStore.getState().refresh());
    },
    [],
  );

  return {
    ensure,
    pause: useCallback(() => captureRef.current?.pause(), []),
    resume: useCallback(() => captureRef.current?.resume(), []),
    stop: useCallback(
      () => captureRef.current?.stop() ?? Promise.resolve(),
      [],
    ),
    online,
    notices: {
      offline: enabled && !online,
      storageFailed: enabled && storageFailed,
      lowStorageBytes:
        enabled && storage?.low && storage.available !== null
          ? storage.available
          : null,
      nearUploadLimit:
        enabled && bytes >= OWNED_UPLOAD_MAX_BYTES * UPLOAD_LIMIT_WARNING_RATIO,
    },
  };
};
